import type React from 'react'
import type { LinkableTextFrame } from '@/lib/text-linking/types/linkable-text-frame'
import { outPortPoint } from '@/lib/text-linking/text-linking-geometry'

import { chromeFrameOf } from '../helpers/chrome-frame-of'
import { hasOutgoing } from '../helpers/port-frames'
import { OutPortPlusGlyph } from './out-port-plus-glyph'
import { PortGroup } from './port-group'

/**
 * Text Tool hover "+" (WMPCM-6852 AC12) — shown for one hovered, linkable frame while the
 * Text Tool is active and not yet in an active linking session (once linking starts, the
 * layer's port passes already highlight every linkable frame). Reuses the exact out-port
 * glyph (`OutPortPlusGlyph`); clicking it enters the same linking mode as the Select-Tool port.
 *
 * No control: it exists only while `textToolHoverFrameId` is set, which only mouse hover
 * ever does, so there is no state in which a keyboard user could reach it. The Select-tool
 * out-port is the keyboard route to the same action.
 */
export function TextToolHoverPort({
  frameId,
  linkableFrames,
  chains,
  scale,
  overflowBadgeFrameIds,
  onOutPortClick,
}: {
  frameId: string
  linkableFrames: LinkableTextFrame[]
  chains: LinkableTextFrame[][]
  scale: number
  overflowBadgeFrameIds: ReadonlySet<string>
  onOutPortClick: (frameId: string) => void
}): React.JSX.Element | null {
  const frame = linkableFrames.find((f) => f.id === frameId)

  if (!frame || hasOutgoing(chains, frame.id)) return null
  const chrome = chromeFrameOf(frame, scale)

  return (
    <PortGroup key="text-tool-hover-port" onClick={() => onOutPortClick(frame.id)} cursor="pointer">
      <OutPortPlusGlyph
        center={outPortPoint(chrome, undefined, chrome.pivot)}
        rotationDeg={frame.rotation ?? 0}
        showContainer={!overflowBadgeFrameIds.has(frame.id)}
      />
    </PortGroup>
  )
}
